import type { ProfileStatTab } from '@/features/profile/types';

export const PROFILE_STAT_TABS: readonly { id: ProfileStatTab; label: string }[] = [
  { id: 'posts', label: 'Posts' },
  { id: 'following', label: 'Following' },
  { id: 'followers', label: 'Followers' },
  { id: 'joined', label: 'Joined classes' },
] as const;

export const ACCOUNT_TYPE_LABELS = {
  student: 'Student',
  instructor: 'Instructor',
  management: 'Staff',
  promoter: 'Promoter',
  parent: 'Parent',
  tutor: 'Tutor',
} as const;

/** Cover art aspect ratio — 4:1 width to height */
export const PROFILE_BANNER_ASPECT_WIDTH = 4;
export const PROFILE_BANNER_ASPECT_HEIGHT = 1;

/** Profile cover when no custom banner is set */
export const profileBannerPlaceholderClassName = 'bg-gray-200';

/** 4:1 aspect ratio — compact full-width profile cover */
export const profileBannerAspectClassName = 'aspect-[4/1] w-full';

/** Profile hero / edit — avatar dimensions (matches Avatar size xl). */
export const profileAvatarSizeClassName = 'h-24 w-24 sm:h-28 sm:w-28';

export const profileAvatarImageClassName =
  'rounded-full border-4 border-white object-cover shadow-sm ring-1 ring-gray-200';

/** Overlap avatar onto cover (edit profile — ~half avatar height). */
export const profileAvatarOverlapClassName = '-mt-14 sm:-mt-16';

/** Floating control on profile cover — shared by hero and edit profile. */
export const profileCoverChangeButtonClassName =
  'absolute left-4 top-3 z-10 inline-flex items-center gap-2 rounded-lg border border-gray-300/90 bg-white/95 px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm backdrop-blur-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00A7C8] focus-visible:ring-offset-1 sm:left-6';

/** Circular camera control on profile photo — matches cover change button tones. */
export const profilePhotoChangeBadgeClassName =
  'flex h-8 w-8 items-center justify-center rounded-full border border-gray-300/90 bg-white/95 shadow-sm ring-2 ring-white backdrop-blur-sm';

/** Owned / archived class card width — sidebar strip and settings grid. */
export const profileOwnedClassCardWidthClassName = 'w-[min(100%,260px)] shrink-0';

/** Wrap multiple class cards in a row (settings, profile mobile scroll). */
export const profileOwnedClassGridClassName = 'flex flex-wrap gap-3';

/** Page shell — matches app content gutters */
export const profilePageShellClassName = 'mx-auto w-full max-w-6xl px-4 sm:px-6';

/**
 * Two-column profile layout: main feed column (42rem / max-w-2xl) + classes sidebar.
 * Main column is left-aligned in the grid so it lines up between header and body.
 */
export const profileContentGridClassName =
  'grid grid-cols-1 gap-x-6 gap-y-6 lg:grid-cols-[minmax(0,42rem)_17.5rem]';

export const profileMainColumnClassName = 'min-w-0 w-full max-w-2xl';

export const profileSidebarColumnClassName =
  'order-first min-w-0 w-full lg:order-none lg:w-[17.5rem] lg:shrink-0';

/** Reserves sidebar width in the header row so the main column aligns with the body grid */
export const profileAsideSpacerClassName = 'hidden lg:block lg:w-[17.5rem] lg:shrink-0';

/** Vertical rhythm between timeline blocks — matches class / global feed */
export const profileStackGapClassName = 'space-y-4 lg:space-y-6';

/** Align classes sidebar with timeline (below in-column stats tabs) on large screens */
export const profileSidebarOffsetClassName = 'lg:pt-12';

/** Simulated profile fetch delay for loading skeleton */
export const PROFILE_LOADING_MS = 450;
